import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { HybridStoreManagement } from './HybridStoreManagement';
import { StoreDetailView } from './StoreDetailView';
import { useStoreManagement } from '../../context/StoreManagementContext';
import { useStoresQuery, useInvalidateStores } from '../../hooks/queries';
import {
  DEFAULT_STORE_FUNCTION,
  STORE_FUNCTIONS,
  tabIndexForFunction,
  type StoreFunction,
} from './storeDetailTabs';

export const StoreRouter: React.FC = () => {
  const { storeId, function: functionParam } = useParams<{
    storeId?: string;
    function?: string;
  }>();
  const navigate = useNavigate();
  const { data: stores = [] } = useStoresQuery();
  const invalidateStores = useInvalidateStores();
  const { clearSelection } = useStoreManagement();

  // Track previous storeId to detect navigation back to list
  const prevStoreIdRef = useRef<string | undefined>(storeId);

  // Clear selection and refresh data only when navigating BACK from detail to list
  useEffect(() => {
    const prevStoreId = prevStoreIdRef.current;
    prevStoreIdRef.current = storeId;

    // Only trigger refresh when going from a store detail (had storeId) back to list (no storeId)
    if (prevStoreId && !storeId) {
      clearSelection();
      invalidateStores();
    }
  }, [storeId, clearSelection, invalidateStores]);

  // If we have a store ID, we're in single store view mode
  if (storeId) {
    // Find the store (stores are guaranteed to be loaded by StoreManagementWrapper)
    const store = stores.find((s) => s.storeNumber === storeId);

    if (!store) {
      // Store not found after loading, redirect to store management
      return <Navigate to="/stores" replace />;
    }

    // Validate function parameter
    const currentFunction = (functionParam as StoreFunction) || DEFAULT_STORE_FUNCTION;

    if (!STORE_FUNCTIONS.includes(currentFunction)) {
      // Invalid function — fall back to the first tab.
      return <Navigate to={`/stores/${storeId}/${DEFAULT_STORE_FUNCTION}`} replace />;
    }

    const handleBack = () => {
      navigate('/stores');
    };

    const handleTabChange = (newFunction: StoreFunction) => {
      navigate(`/stores/${storeId}/${newFunction}`);
    };

    const handleEdit = (field: string, value: any) => {
      // TODO: Implement actual save logic
    };

    // Render store detail view with routing support
    return (
      <StoreDetailView
        store={store}
        onBack={handleBack}
        onEdit={handleEdit}
        initialTab={tabIndexForFunction(currentFunction)}
        onTabChange={handleTabChange}
      />
    );
  }

  // No store ID, show the main store management view
  return <HybridStoreManagement />;
};

export default StoreRouter;
